import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { ShieldCheck, Search, XCircle, Award } from "lucide-react";

export default function CertificateVerify() {
  const [params] = useSearchParams();
  const { id: pathId } = useParams();
  const [code, setCode] = useState(pathId || params.get("id") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const runVerify = useCallback(async (raw) => {
    const id = (raw || "").trim().toUpperCase();
    if (!id) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/certificates/verify/${encodeURIComponent(id)}`);
      setResult(data);
    } catch (err) {
      setError(errorMessage(err) || "Certificate not found");
    } finally { setLoading(false); }
  }, []);

  // Auto-verify when arriving via /verify-certificate/<id> (e.g. QR scan)
  useEffect(() => {
    const initial = pathId || params.get("id");
    if (initial) runVerify(initial);
  }, [pathId, params, runVerify]);

  const verify = (e) => { e?.preventDefault(); runVerify(code); };

  return (
    <div className="App" data-testid="verify-cert-page">
      <Navbar />
      <div className="relative">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24 min-h-[60vh]">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start relative">
              <div className="flex items-center gap-2 label-eyebrow mb-3"><ShieldCheck className="w-4 h-4 text-brand" /> Certificate Verification</div>
              <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight leading-none">Verify a<br />certificate.</h1>
              <p className="text-gray-400 mt-5 max-w-sm leading-relaxed">Enter the verification ID printed on the certificate (format <span className="font-mono text-gray-300">NSI-2026-XXXX</span>) to confirm its authenticity.</p>
            </div>

            <div className="lg:col-span-7">
              <form onSubmit={verify} className="flex flex-col sm:flex-row gap-3" data-testid="verify-form">
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="NSI-2026-XXXX"
                  className="input-field flex-1 font-mono uppercase tracking-widest" data-testid="verify-code-input" />
                <button type="submit" disabled={loading} className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap" data-testid="verify-submit-btn">
                  {loading ? <><span className="spinner" /> Verifying…</> : <><Search className="w-4 h-4" /> Verify</>}
                </button>
              </form>

              {error && (
                <div className="mt-8 card-surface border-l-2 border-red-500 flex items-center gap-3" data-testid="verify-error">
                  <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                  <div><div className="text-white font-medium">Not verified</div><div className="text-sm text-gray-400">{error}</div></div>
                </div>
              )}

              {result && (
                <div className="mt-8 card-surface border-l-2 border-emerald-500" data-testid="verify-result">
                  <div className="flex items-center gap-3 mb-5">
                    <Award className="w-7 h-7 text-emerald-400" />
                    <div>
                      <div className="text-emerald-400 font-medium">Authentic certificate</div>
                      <div className="text-xs text-gray-500 font-mono">{result.verification_id}</div>
                    </div>
                  </div>
                  <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                    <Field label="Certificate type" value={result.label} testid="vr-type" />
                    <Field label="Holder name" value={result.holder_name} testid="vr-holder" />
                    <Field label="Team" value={result.team_name} testid="vr-team" />
                    <Field label="Project title" value={result.project_title} testid="vr-project" />
                    <Field label="Edition" value={result.edition_label} testid="vr-edition" />
                    <Field label="Date issued" value={result.issued_date} testid="vr-date" />
                    {result.rank_label && <Field label="Rank" value={result.rank_label} testid="vr-rank" />}
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Field = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <dt className="label-eyebrow mb-1">{label}</dt>
    <dd className="text-white">{value || "—"}</dd>
  </div>
);
